import React, { useState, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import GuestLayout from "../../Layouts/GuestLayout";
import { TextField } from "@mui/material";
import SecondaryButton from "../../Components/SecondaryButton";
import GroupedContainer from "../../Components/GroupedContainer";
import SignatureCanvas from "react-signature-canvas";

export default function DriverRegister() {
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [trailerType, setTrailerType] = useState('');
  const [signatureFile, setSignatureFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const sigCanvas = useRef(null);

  // Clears signature canvas and file state
  const clearSignature = () => {
    sigCanvas.current.clear();
    setSignatureFile(null);
  };

  // Captures and validates signature, converting to a File
  const handleSignature = () => {
    const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
    const file = dataURLtoFile(signatureData, 'signature.png');
    if (file.type.match(/image\/(jpeg|png|jpg|gif|svg)/) && file.size <= 2048 * 1024) {
      setSignatureFile(file);
      setFieldErrors(prev => ({ ...prev, signature: '' }));
    } else {
      Swal.fire({
        title: 'Error!',
        text: 'Signature must be an image file (jpeg, png, jpg, gif, svg) and not larger than 2MB.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  // Utility to convert data URL to File
  const dataURLtoFile = (dataurl, filename) => {
    let arr = dataurl.split(',');
    let mime = arr[0].match(/:(.*?);/)[1];
    let bstr = atob(arr[1]);
    let n = bstr.length;
    let u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Validates required fields before submit
  const validateFields = () => {
    const errors = {};
    if (!email) errors.email = 'Email is required';
    if (!firstName) errors.firstName = 'First name is required';
    if (!lastName) errors.lastName = 'Last name is required';
    if (!phone) errors.phone = 'Phone number is required';
    if (!username) errors.username = 'Username is required';
    if (!password) errors.password = 'Password is required';
    if (password !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (!trailerType) errors.trailerType = 'Trailer type is required';
    if (!signatureFile) errors.signature = 'Signature is required';
    return errors;
  };

  // Sends form data to API
  // Sends form data to API
  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');
    setMessage('');

    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setIsLoading(false);
      return;
    }

    try {
      const partnerCompanyId = localStorage.getItem('companyId') || 1;
      const formData = new FormData();
      formData.append('email', email);
      formData.append('user_role_id', 2);
      formData.append('first_name', firstName);
      formData.append('last_name', lastName);
      formData.append('phone', phone);
      formData.append('username', username);
      formData.append('password', password);
      formData.append('c_password', confirmPassword);
      formData.append('trailer_type', trailerType);
      formData.append('signature', signatureFile);

      // Incluir cabeçalhos de autenticação antes de enviar
      const headers = {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Screen-Name': '/NewLoad'
      };

      await axios.post(
        'https://new.cajuu.app/api/user-driver',
        { headers },
        formData,
      );

      setMessage('User and driver created successfully.');
      Swal.fire({
        title: 'Success!',
        text: 'User and driver created successfully.',
        icon: 'success',
        confirmButtonText: 'Ok'
      }).then(() => window.location.reload());

      localStorage.removeItem('companyId');
    } catch (err) {
      setError('Registration not completed! An error occurred.');

      if (err.response && err.response.data) {
        const apiMessage = err.response.data.message || 'Validation error';
        const apiErrors = err.response.data.errors || {};
        setFieldErrors(apiErrors);

        Swal.fire({
          title: 'Error!',
          text: `Registration failed: ${apiMessage}`,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: 'Registration not completed! An unknown error occurred.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <GuestLayout title="Driver Register">
      {/* Messages */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {message}
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="w-full mt-5 flex justify-center items-center">
        <div className="flex text-2xl sm:text-3xl tracking-tight text-amber-500 font-semibold">
          Sign up to
          <div className="text-white flex items-center">
            <img
              src="/img/cajuu.png"
              alt="Metr Logo"
              className="w-7 h-7 sm:w-9 sm:h-9 ml-2"
            />
            Cajuu<span className="text-[#2f2f2f]/40">.</span>
          </div>
        </div>
      </div>

      {/* Form fields */}
      <GroupedContainer className="w-full mt-8 sm:mt-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <TextField
            label="First Name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            required
            color="secondary"
            className="w-full"
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            required
            color="secondary"
            className="w-full"
          />
        </div>
      </GroupedContainer>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 mt-7">
        <TextField
          label="Username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          required
          color="secondary"
          className="w-full"
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          color="secondary"
          className="w-full"
        />
        <TextField
          label="Phone"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          required
          color="secondary"
          className="w-full"
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          color="secondary"
          className="w-full"
        />
        <TextField
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          required
          color="secondary"
          className="w-full"
        />
      </div>

      {/* Trailer Type */}
      <div className="mt-7">
        <label className="block text-lg font-medium text-gray-200 mb-2">Trailer Type</label>
        <div className="flex items-center gap-4 sm:gap-6">
          <input
            type="radio"
            id="open"
            name="trailer"
            value="open"
            className="hidden peer"
            checked={trailerType === 'open'}
            onChange={e => setTrailerType(e.target.value)}
          />
          <label
            htmlFor="open"
            className="flex items-center justify-center px-4 py-2 border rounded-md cursor-pointer transition hover:bg-gray-700 peer-checked:bg-amber-600 peer-checked:border-amber-600 peer-checked:text-white"
          >
            Open
          </label>
          <input
            type="radio"
            id="enclosed"
            name="trailer"
            value="enclosed"
            className="hidden peer"
            checked={trailerType === 'enclosed'}
            onChange={e => setTrailerType(e.target.value)}
          />
          <label
            htmlFor="enclosed"
            className="flex items-center justify-center px-4 py-2 border rounded-md cursor-pointer transition hover:bg-gray-700 peer-checked:bg-amber-600 peer-checked:border-amber-600 peer-checked:text-white"
          >
            Enclosed
          </label>
        </div>
      </div>

      {/* Signature */}
      <div className="mt-7">
        <label className="block text-lg font-medium text-gray-200 mb-2">Signature</label>
        <SignatureCanvas
          ref={sigCanvas}
          penColor="white"
          canvasProps={{
            height: 150,
            className: "w-full border-2 rounded-[1.5rem] border-gray-300"
          }}
          onEnd={handleSignature}
        />
        <div className="flex justify-between mt-4">
          <button onClick={clearSignature} className="bg-red-500 rounded-lg text-white h-12 w-full sm:w-1/2 mr-2">
            Clear
          </button>
          <button onClick={handleSignature} className="bg-amber-500 rounded-lg text-white h-12 w-full sm:w-1/2 ml-2">
            Save
          </button>
        </div>
        {fieldErrors.signature && <span className="text-red-500 text-sm">{fieldErrors.signature}</span>}
      </div>

      {/* Submit Button */}
      <div className="mt-10">
        <SecondaryButton
          onClick={handleSubmit}
          disabled={isLoading}
          className="bg-amber-500 hover:bg-amber-600 text-white h-12 w-full"
        >
          {isLoading ? 'Registering...' : 'Submit'}
        </SecondaryButton>

        <a href="/login" className="underline w-max block mx-auto mt-4 text-sm font-medium text-white hover:text-amber-500">
          Already have an account?
        </a>
      </div>
    </GuestLayout>
  );
}
